<template>
  <div class="animated fadeIn lunch-schedule-page">
    <div class="card">
      <div class="main-card-body">
        <div class="body-header lunch-schedule-header">
          <div class="lunch-schedule-header__content">
            <h4 class="card-header-title">QUẢN LÝ THỰC ĐƠN</h4>
            <p class="lunch-schedule-subtitle">
              Thiết lập món ăn được phục vụ theo từng ngày.
            </p>
          </div>
          <div class="lunch-schedule-header__actions">
            <button type="button" class="lunch-outline-button" @click="goBackToRegistration">
              <i class="fa fa-arrow-left"></i>
              <span>Quay lại</span>
            </button>
            <button
              v-if="canManageLunchFoods"
              type="button"
              class="lunch-primary-button"
              @click="goToLunchMenuManagement"
            >
              <i class="fa fa-cutlery"></i>
              <span>Quản lý danh sách món</span>
            </button>
          </div>
        </div>

        <section class="lunch-workspace">
          <aside class="lunch-schedule-card lunch-calendar-panel">
            <div class="lunch-section-heading">
              <div class="lunch-section-title">
              <h5>LỊCH THỰC ĐƠN</h5>
              <span>Chọn một hoặc nhiều ngày để áp dụng cùng danh sách món.</span>
              </div>
              <button
                type="button"
                class="lunch-month-select-button"
                :class="{ 'lunch-month-select-button--active': isEveryDayInMonthSelected }"
                @click="toggleSelectEveryDayInMonth"
              >
                <i class="fa" :class="isEveryDayInMonthSelected ? 'fa-times' : 'fa-calendar-check-o'"></i>
                <span>Áp dụng mọi ngày</span>
              </button>
            </div>

            <div class="lunch-calendar-header">
              <button type="button" class="lunch-calendar-nav" aria-label="Tháng trước" @click="changeMonth(-1)">
                <i class="fa fa-angle-left"></i>
              </button>
              <h5>{{ calendarMonthDisplay }}</h5>
              <button type="button" class="lunch-calendar-nav" aria-label="Tháng sau" @click="changeMonth(1)">
                <i class="fa fa-angle-right"></i>
              </button>
            </div>

            <div class="lunch-weekday-grid">
              <span v-for="weekday in calendarWeekdays" :key="weekday">{{ weekday }}</span>
            </div>
            <div class="lunch-calendar-grid">
              <button
                v-for="day in calendarDays"
                :key="day.key || day.placeholderKey"
                type="button"
                class="lunch-calendar-day"
                :class="getCalendarDayClass(day)"
                :title="day.tooltip"
                :disabled="day.isPlaceholder"
                @click="handleCalendarDayClick(day)"
              >
                <span class="lunch-calendar-day__number">{{ day.day }}</span>
                <span v-if="day.hasMenu" class="lunch-calendar-day__menu-mark">
                  <i class="fa fa-check"></i>
                </span>
              </button>
            </div>

            <div class="lunch-calendar-legend">
              <span><i class="fa fa-check"></i> = Đã có thực đơn</span>
            </div>

          </aside>

          <main class="lunch-schedule-card lunch-food-section">
            <div class="lunch-food-heading">
              <div class="lunch-section-title">
                <h5>CHỌN MÓN CHO THỰC ĐƠN</h5>
                <span>Chỉ hiển thị các món đang áp dụng trong danh sách món.</span>
              </div>
              <div class="lunch-save-row">
                <strong>Đã chọn {{ selectedFoodIds.length }} món</strong>
                <button
                  type="button"
                  class="lunch-primary-button lunch-save-button"
                  :disabled="!canSave"
                  @click="handleSave"
                >
                  <i class="fa fa-save"></i>
                  <span>Lưu thực đơn</span>
                </button>
              </div>
            </div>

            <section v-if="mixedMenuWarning" class="lunch-inline-warning">
              Các ngày đã chọn hiện có thực đơn khác nhau. Hãy chọn danh sách món mới để áp dụng chung cho tất cả ngày.
            </section>

            <div class="lunch-management-toolbar">
              <div class="lunch-filter-list">
                <button
                  v-for="category in categoriesWithAll"
                  :key="category"
                  type="button"
                  class="lunch-filter-button"
                  :class="{ 'lunch-filter-button--active': activeCategory === category }"
                  @click="activeCategory = category"
                >
                  {{ category }}
                </button>
              </div>

              <el-checkbox
                v-model="selectAllFoodsChecked"
                :indeterminate="isFoodSelectAllIndeterminate"
                class="lunch-select-all-checkbox"
              >
                Chọn tất cả món
              </el-checkbox>

              <el-input
                v-model="searchKeyword"
                clearable
                class="lunch-search-input"
                placeholder="Tìm kiếm món ăn..."
                prefix-icon="el-icon-search"
              />
            </div>

            <div v-if="!filteredFoods.length" class="lunch-empty-state">
              Không tìm thấy món ăn phù hợp.
            </div>

            <div v-else class="lunch-food-grid">
              <article
                v-for="food in filteredFoods"
                :key="food.id"
                class="lunch-food-card"
                :class="{ 'lunch-food-card--selected': isFoodSelected(food.id) }"
                @click="toggleFood(food.id)"
              >
                <div class="lunch-food-card__image">
                  <img :src="food.image" :alt="food.name" />
                  <span v-if="isFoodSelected(food.id)" class="lunch-food-card__check">
                    <i class="fa fa-check"></i>
                  </span>
                </div>
                <div class="lunch-food-card__body">
                  <span class="lunch-food-card__category">{{ food.category }}</span>
                  <h6>{{ food.name }}</h6>
                  <p>{{ food.description || "Món ăn trong thực đơn trưa" }}</p>
                  <div class="lunch-food-card__footer">
                    <strong>{{ formatCurrency(food.price) }}</strong>
                    <el-button
                      size="mini"
                      round
                      :type="isFoodSelected(food.id) ? 'primary' : 'default'"
                      :plain="!isFoodSelected(food.id)"
                      class="lunch-select-button"
                      @click.stop="toggleFood(food.id)"
                    >
                      <i class="fa" :class="isFoodSelected(food.id) ? 'fa-check' : 'fa-plus'"></i>
                      <span>{{ isFoodSelected(food.id) ? "Đã chọn" : "Chọn" }}</span>
                    </el-button>
                  </div>
                </div>
              </article>
            </div>

          </main>
        </section>
      </div>
    </div>

    <el-dialog
      :title="editingDate ? `THỰC ĐƠN NGÀY ${formatDate(editingDate)}` : 'THỰC ĐƠN'"
      :visible.sync="editDialogVisible"
      width="920px"
      custom-class="lunch-menu-edit-dialog"
      @closed="resetEditDialog"
    >
      <div class="lunch-edit-toolbar">
        <div class="lunch-filter-list">
          <button
            v-for="category in categoriesWithAll"
            :key="`edit-${category}`"
            type="button"
            class="lunch-filter-button"
            :class="{ 'lunch-filter-button--active': editingCategory === category }"
            @click="editingCategory = category"
          >
            {{ category }}
          </button>
        </div>
        <el-input
          v-model="editingSearchKeyword"
          clearable
          class="lunch-search-input"
          placeholder="Tìm kiếm món ăn..."
          prefix-icon="el-icon-search"
        />
      </div>

      <div v-if="!filteredEditingFoods.length" class="lunch-empty-state">
        Không tìm thấy món ăn phù hợp.
      </div>
      <div v-else class="lunch-food-grid lunch-food-grid--modal">
        <article
          v-for="food in filteredEditingFoods"
          :key="`edit-food-${food.id}`"
          class="lunch-food-card"
          :class="{ 'lunch-food-card--selected': isEditingFoodSelected(food.id) }"
          @click="toggleEditingFood(food.id)"
        >
          <div class="lunch-food-card__image">
            <img :src="food.image" :alt="food.name" />
            <span v-if="isEditingFoodSelected(food.id)" class="lunch-food-card__check">
              <i class="fa fa-check"></i>
            </span>
          </div>
          <div class="lunch-food-card__body">
            <span class="lunch-food-card__category">{{ food.category }}</span>
            <h6>{{ food.name }}</h6>
            <p>{{ food.description || "Món ăn trong thực đơn trưa" }}</p>
            <div class="lunch-food-card__footer">
              <strong>{{ formatCurrency(food.price) }}</strong>
              <el-button
                size="mini"
                round
                :type="isEditingFoodSelected(food.id) ? 'primary' : 'default'"
                :plain="!isEditingFoodSelected(food.id)"
                class="lunch-select-button"
                @click.stop="toggleEditingFood(food.id)"
              >
                <i class="fa" :class="isEditingFoodSelected(food.id) ? 'fa-check' : 'fa-plus'"></i>
                <span>{{ isEditingFoodSelected(food.id) ? "Đã chọn" : "Chọn" }}</span>
              </el-button>
            </div>
          </div>
        </article>
      </div>

      <span slot="footer" class="dialog-footer lunch-dialog-footer">
        <button type="button" class="lunch-danger-button" @click="confirmDeleteEditingMenu">
          <i class="fa fa-trash"></i>
          <span>Xóa thực đơn</span>
        </button>
        <span class="lunch-dialog-footer__actions">
          <button type="button" class="lunch-outline-button" @click="editDialogVisible = false">
            Hủy
          </button>
          <button type="button" class="lunch-primary-button" @click="saveEditingMenu">
            <i class="fa fa-save"></i>
            <span>Lưu thay đổi</span>
          </button>
        </span>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import MethodService from "@/service/MethodService";
import {
  deleteMenuForDate,
  getActiveLunchFoods,
  getExistingMenuDates,
  getLunchDateMetadata,
  getLunchFoodCategories,
  getMenuFoodIdsByDate,
  getMenusByDates,
  hasExistingMenu,
  loadLunchFoods,
  loadMenuCalendar,
  lunchMenuState,
  saveMenuForDates,
  updateMenuForDate
} from "./lunchMenuStore";

const ALL_CATEGORY = "Tất cả";
const CALENDAR_WEEKDAYS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
const DAY_IN_MS = 86400000;
const MAX_OVERWRITE_DATE_RANGES = 4;

export default {
  name: "LunchMenuScheduleManagement",
  data() {
    const today = this.formatDateKey(new Date());
    return {
      activeCategory: ALL_CATEGORY,
      calendarDate: this.createDate(today),
      editDialogVisible: false,
      editingCategory: ALL_CATEGORY,
      editingDate: "",
      editingFoodIds: [],
      editingSearchKeyword: "",
      loading: false,
      mixedMenuWarning: false,
      searchKeyword: "",
      selectedDates: [],
      selectedFoodIds: []
    };
  },
  async created() {
    await this.loadPageData();
  },
  computed: {
    canManageLunchFoods() {
      return MethodService.checkHaveFeature("FT2401");
    },
    calendarWeekdays() {
      return CALENDAR_WEEKDAYS;
    },
    calendarMonthDisplay() {
      const month = this.calendarDate.getMonth() + 1;
      return `Tháng ${String(month).padStart(2, "0")}/${this.calendarDate.getFullYear()}`;
    },
    categoriesWithAll() {
      return [ALL_CATEGORY, ...getLunchFoodCategories()];
    },
    activeFoods() {
      return getActiveLunchFoods();
    },
    displayedMonthDateKeys() {
      const year = this.calendarDate.getFullYear();
      const month = this.calendarDate.getMonth();
      const totalDays = new Date(year, month + 1, 0).getDate();
      const dates = [];

      for (let day = 1; day <= totalDays; day += 1) {
        dates.push(this.formatDateKey(new Date(year, month, day)));
      }

      return dates;
    },
    autoSelectableMonthDateKeys() {
      return this.displayedMonthDateKeys.filter((dateKey) => {
        const metadata = getLunchDateMetadata(dateKey);
        if (metadata) return metadata.is_working_day !== false;
        const date = this.createDate(dateKey);
        return date.getDay() !== 0 && date.getDay() !== 6;
      });
    },
    filteredFoods() {
      return this.filterFoods(this.activeFoods, this.activeCategory, this.searchKeyword);
    },
    filteredEditingFoods() {
      return this.filterFoods(this.activeFoods, this.editingCategory, this.editingSearchKeyword);
    },
    canSave() {
      return this.selectedDates.length > 0 && this.selectedFoodIds.length > 0;
    },
    selectedActiveFoodIds() {
      const activeFoodIds = this.activeFoods.map((food) => food.id);
      return this.selectedFoodIds.filter((foodId) => activeFoodIds.includes(foodId));
    },
    isEveryDayInMonthSelected() {
      return (
        this.autoSelectableMonthDateKeys.length > 0 &&
        this.autoSelectableMonthDateKeys.every((dateKey) => this.selectedDates.includes(dateKey))
      );
    },
    isAllActiveFoodsSelected() {
      return (
        this.activeFoods.length > 0 &&
        this.selectedActiveFoodIds.length === this.activeFoods.length
      );
    },
    isFoodSelectAllIndeterminate() {
      return (
        this.selectedActiveFoodIds.length > 0 &&
        this.selectedActiveFoodIds.length < this.activeFoods.length
      );
    },
    selectAllFoodsChecked: {
      get() {
        return this.isAllActiveFoodsSelected;
      },
      set(checked) {
        this.toggleSelectAllFoods(checked);
      }
    },
    calendarDays() {
      const year = this.calendarDate.getFullYear();
      const month = this.calendarDate.getMonth();
      const firstDate = new Date(year, month, 1);
      const totalDays = new Date(year, month + 1, 0).getDate();
      const firstWeekdayIndex = (firstDate.getDay() + 6) % 7;
      const days = [];

      for (let index = 0; index < firstWeekdayIndex; index += 1) {
        days.push({
          isPlaceholder: true,
          placeholderKey: `blank-${year}-${month}-${index}`
        });
      }

      for (let day = 1; day <= totalDays; day += 1) {
        const date = new Date(year, month, day);
        const dateKey = this.formatDateKey(date);
        const metadata = getLunchDateMetadata(dateKey);
        const isWeekend = metadata ? metadata.is_weekend : date.getDay() === 0 || date.getDay() === 6;
        const isHoliday = metadata ? metadata.is_holiday : false;
        days.push({
          key: dateKey,
          day,
          dateKey,
          hasMenu: hasExistingMenu(dateKey),
          isSelected: this.selectedDates.includes(dateKey),
          isToday: dateKey === this.formatDateKey(new Date()),
          isWeekend,
          isHoliday,
          tooltip: isHoliday || isWeekend ? "Ngày nghỉ" : "",
          isPlaceholder: false
        });
      }

      return days;
    },
    scheduleVersion() {
      return Object.keys(lunchMenuState.menuSchedules)
        .map((dateKey) => `${dateKey}:${lunchMenuState.menuSchedules[dateKey].join(",")}`)
        .join("|");
    }
  },
  watch: {
    selectedDates() {
      this.syncSelectedFoodsFromDates();
    },
    scheduleVersion() {
      this.syncSelectedFoodsFromDates();
    }
  },
  methods: {
    goBackToRegistration() {
      this.$router.push({ path: "/admin/lunch-registration" }).catch(() => {});
    },
    goToLunchMenuManagement() {
      this.$router.push({ path: "/admin/lunch-menu-management" }).catch(() => {});
    },
    createDate(dateKey) {
      const [year, month, day] = dateKey.split("-").map(Number);
      return new Date(year, month - 1, day);
    },
    formatDateKey(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatDate(dateKey) {
      const [year, month, day] = dateKey.split("-");
      return `${day}/${month}/${year}`;
    },
    formatMonthKey(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    },
    async loadPageData() {
      this.loading = true;
      try {
        await Promise.all([loadLunchFoods({ isActive: true }), this.loadCalendar()]);
      } catch (error) {
        this.showApiError(error, "Không tải được dữ liệu thực đơn.");
      } finally {
        this.loading = false;
      }
    },
    async loadCalendar() {
      await loadMenuCalendar(this.formatMonthKey(this.calendarDate));
    },
    formatCurrency(value) {
      return `${Number(value || 0).toLocaleString("vi-VN")} đ`;
    },
    filterFoods(foods, category, keyword) {
      const normalizedKeyword = keyword.trim().toLowerCase();
      return foods.filter((food) => {
        const matchesCategory = category === ALL_CATEGORY || food.category === category;
        const matchesKeyword =
          !normalizedKeyword ||
          food.name.toLowerCase().includes(normalizedKeyword) ||
          (food.description || "").toLowerCase().includes(normalizedKeyword);
        return matchesCategory && matchesKeyword;
      });
    },
    changeMonth(offset) {
      this.calendarDate = new Date(
        this.calendarDate.getFullYear(),
        this.calendarDate.getMonth() + offset,
        1
      );
      this.loadCalendar().catch((error) => {
        this.showApiError(error, "Không tải được lịch thực đơn.");
      });
    },
    getCalendarDayClass(day) {
      return {
        "lunch-calendar-day--menu": day.hasMenu,
        "lunch-calendar-day--placeholder": day.isPlaceholder,
        "lunch-calendar-day--selected": day.isSelected,
        "lunch-calendar-day--today": day.isToday,
        "lunch-calendar-day--weekend": day.isWeekend,
        "lunch-calendar-day--holiday": day.isHoliday
      };
    },
    handleCalendarDayClick(day) {
      if (!day || !day.dateKey) return;
      if (day.isSelected) {
        this.removeDate(day.dateKey);
        return;
      }
      if (day.hasMenu) {
        this.openEditDialog(day.dateKey);
        return;
      }
      this.toggleDate(day.dateKey);
    },
    toggleSelectEveryDayInMonth() {
      const monthDates = this.autoSelectableMonthDateKeys;
      if (this.isEveryDayInMonthSelected) {
        this.selectedDates = this.selectedDates.filter((dateKey) => !monthDates.includes(dateKey));
        return;
      }
      this.selectedDates = [...new Set([...this.selectedDates, ...monthDates])].sort();
    },
    toggleDate(dateKey) {
      if (this.selectedDates.includes(dateKey)) {
        this.removeDate(dateKey);
        return;
      }
      this.selectedDates = [...this.selectedDates, dateKey].sort();
    },
    removeDate(dateKey) {
      this.selectedDates = this.selectedDates.filter((item) => item !== dateKey);
    },
    syncSelectedFoodsFromDates() {
      this.mixedMenuWarning = false;
      if (!this.selectedDates.length) {
        this.selectedFoodIds = [];
        return;
      }
      if (this.selectedDates.length === 1) {
        this.selectedFoodIds = getMenuFoodIdsByDate(this.selectedDates[0]);
        return;
      }
      const signatures = this.selectedDates.map((dateKey) =>
        getMenuFoodIdsByDate(dateKey)
          .slice()
          .sort((a, b) => String(a).localeCompare(String(b)))
          .join(",")
      );
      const uniqueSignatures = [...new Set(signatures)];
      if (uniqueSignatures.length === 1 && signatures[0]) {
        this.selectedFoodIds = getMenuFoodIdsByDate(this.selectedDates[0]);
      } else if (uniqueSignatures.some(Boolean)) {
        this.selectedFoodIds = [];
        this.mixedMenuWarning = true;
      } else {
        this.selectedFoodIds = [];
      }
    },
    isFoodSelected(foodId) {
      return this.selectedFoodIds.includes(foodId);
    },
    toggleFood(foodId) {
      if (this.isFoodSelected(foodId)) {
        this.selectedFoodIds = this.selectedFoodIds.filter((id) => id !== foodId);
        return;
      }
      this.selectedFoodIds = [...this.selectedFoodIds, foodId];
    },
    toggleSelectAllFoods(checked) {
      if (!checked) {
        this.selectedFoodIds = [];
        return;
      }
      this.selectedFoodIds = this.activeFoods.map((food) => food.id);
      this.mixedMenuWarning = false;
    },
    handleSave() {
      if (!this.canSave) return;
      const existingDates = getExistingMenuDates(this.selectedDates);
      if (existingDates.length) {
        this.confirmOverwrite(existingDates);
        return;
      }
      this.saveSelectedMenu();
    },
    confirmOverwrite(existingDates) {
      const dateCount = `${new Set(existingDates).size} ngày`;
      const summaryText = `Có ${dateCount} đã có thực đơn:`;
      const dateText = this.formatOverwriteDateRanges(existingDates);
      const replacementText = "Tiếp tục sẽ thay thế thực đơn của các ngày trên.";
      const plainMessage = [summaryText, dateText, replacementText].join("\n\n");
      if (this.$confirm) {
        const message = this.$createElement
          ? this.$createElement("div", { class: "lunch-overwrite-message" }, [
            this.$createElement("strong", { class: "lunch-overwrite-message__summary" }, summaryText),
            this.$createElement(
              "div",
              { class: "lunch-overwrite-message__dates" },
              dateText.split("\n").map((date) => this.$createElement("span", date))
            ),
            this.$createElement("strong", replacementText)
          ])
          : plainMessage;
        this.$confirm(message, "Xác nhận thay thế thực đơn", {
          confirmButtonText: "Tiếp tục",
          cancelButtonText: "Hủy",
          type: "warning",
          customClass: "lunch-menu-overwrite-confirm"
        })
          .then(this.saveSelectedMenu)
          .catch(() => {});
        return;
      }
      if (window.confirm(plainMessage)) {
        this.saveSelectedMenu();
      }
    },
    formatOverwriteDateRanges(dateKeys) {
      const dates = [...new Set(dateKeys)]
        .map((dateKey) => {
          const [year, month, day] = dateKey.split("-").map(Number);
          return {
            dateKey,
            year,
            month,
            day,
            timestamp: Date.UTC(year, month - 1, day)
          };
        })
        .sort((a, b) => a.timestamp - b.timestamp);
      const ranges = [];

      dates.forEach((date) => {
        const currentRange = ranges[ranges.length - 1];
        if (currentRange && date.timestamp === currentRange.end.timestamp + DAY_IN_MS) {
          currentRange.end = date;
        } else {
          ranges.push({ start: date, end: date });
        }
      });

      const pad = (value) => String(value).padStart(2, "0");
      const formatFullDate = (date) => `${pad(date.day)}/${pad(date.month)}/${date.year}`;
      const visibleRanges = ranges.slice(0, MAX_OVERWRITE_DATE_RANGES);
      const visibleDateCount = visibleRanges.reduce(
        (total, range) => total + (range.end.timestamp - range.start.timestamp) / DAY_IN_MS + 1,
        0
      );
      const rangeLines = visibleRanges.map(({ start, end }) => {
        if (start.dateKey === end.dateKey) return formatFullDate(start);
        if (start.year === end.year && start.month === end.month) {
          return `${pad(start.day)}–${formatFullDate(end)}`;
        }
        if (start.year === end.year) {
          return `${pad(start.day)}/${pad(start.month)}–${formatFullDate(end)}`;
        }
        return `${formatFullDate(start)}–${formatFullDate(end)}`;
      });
      const hiddenDateCount = dates.length - visibleDateCount;
      if (hiddenDateCount > 0) {
        rangeLines.push(`và ${hiddenDateCount} ngày khác`);
      }
      return rangeLines.join("\n");
    },
    async saveSelectedMenu() {
      try {
        await saveMenuForDates(this.selectedDates, this.selectedFoodIds, true);
        this.mixedMenuWarning = false;
        this.selectedDates = [];
        this.selectedFoodIds = [];
        this.showToast("success", "Lưu thực đơn thành công");
      } catch (error) {
        this.showApiError(error, "Không lưu được thực đơn.");
      }
    },
    async openEditDialog(dateKey) {
      this.editingDate = dateKey;
      if (!getMenuFoodIdsByDate(dateKey).length) {
        try {
          await getMenusByDates([dateKey]);
        } catch (error) {
          this.showApiError(error, "Không tải được thực đơn ngày đã chọn.");
        }
      }
      this.editingFoodIds = getMenuFoodIdsByDate(dateKey);
      this.editingCategory = ALL_CATEGORY;
      this.editingSearchKeyword = "";
      this.editDialogVisible = true;
    },
    resetEditDialog() {
      this.editingDate = "";
      this.editingFoodIds = [];
      this.editingCategory = ALL_CATEGORY;
      this.editingSearchKeyword = "";
    },
    isEditingFoodSelected(foodId) {
      return this.editingFoodIds.includes(foodId);
    },
    toggleEditingFood(foodId) {
      if (this.isEditingFoodSelected(foodId)) {
        this.editingFoodIds = this.editingFoodIds.filter((id) => id !== foodId);
        return;
      }
      this.editingFoodIds = [...this.editingFoodIds, foodId];
    },
    async saveEditingMenu() {
      if (!this.editingDate) return;
      if (!this.editingFoodIds.length) {
        this.confirmDeleteEditingMenu(true);
        return;
      }
      try {
        await updateMenuForDate(this.editingDate, this.editingFoodIds);
        this.editDialogVisible = false;
        this.showToast("success", "Cập nhật thực đơn thành công.");
      } catch (error) {
        await this.handleMenuMutationError(error, "Không lưu được thay đổi thực đơn.");
      }
    },
    confirmDeleteEditingMenu(fromEmptySave = false) {
      if (!this.editingDate) return;
      const title = fromEmptySave ? "Không còn món nào trong thực đơn." : "Xóa thực đơn";
      const message = fromEmptySave
        ? `Bạn có muốn xóa thực đơn ngày ${this.formatDate(this.editingDate)} không?`
        : [
            `Bạn có chắc muốn xóa thực đơn ngày ${this.formatDate(this.editingDate)} không?`,
            "Thao tác này sẽ làm ngày này không còn thực đơn."
          ].join("\n\n");
      if (this.$confirm) {
        this.$confirm(message, title, {
          confirmButtonText: "Xóa thực đơn",
          cancelButtonText: "Hủy",
          type: "warning"
        })
          .then(() => this.deleteEditingMenu())
          .catch(() => {});
        return;
      }
      if (window.confirm(`${title}\n\n${message}`)) {
        this.deleteEditingMenu();
      }
    },
    async deleteEditingMenu() {
      if (!this.editingDate) return;
      try {
        const deletedDate = this.editingDate;
        await deleteMenuForDate(deletedDate);
        this.selectedDates = this.selectedDates.filter((dateKey) => dateKey !== deletedDate);
        await this.loadCalendar();
        this.editDialogVisible = false;
        this.showToast("success", "Xóa thực đơn thành công.");
      } catch (error) {
        await this.handleMenuMutationError(error, "Không xóa được thực đơn.");
      }
    },
    async handleMenuMutationError(error, fallback) {
      const code = this.getApiErrorCode(error);
      if (code === "MENU_NOT_FOUND" || code === "LUNCH_MENU_NOT_FOUND") {
        this.editDialogVisible = false;
        await this.loadCalendar().catch(() => {});
      }
      this.showApiError(error, fallback);
    },
    getApiErrorCode(error) {
      return (
        (error && error.result && (error.result.error_code || error.result.code)) ||
        (error && (error.error_code || error.code)) ||
        ""
      );
    },
    getApiErrorMessage(error, fallback) {
      const result = (error && error.result) || {};
      const code = this.getApiErrorCode(error);
      if (code === "MENU_HAS_REGISTRATIONS") {
        const count = result.registration_count || result.registrationCount || 0;
        const dateText = this.editingDate ? ` ngày ${this.formatDate(this.editingDate)}` : "";
        return `Không thể xóa thực đơn${dateText} vì đã có ${count} người đăng ký.`;
      }
      if (code === "MENU_NOT_FOUND" || code === "LUNCH_MENU_NOT_FOUND") {
        return "Thực đơn của ngày này không còn tồn tại.";
      }
      if (code === "FOOD_ALREADY_REGISTERED") {
        return "Không thể xóa món đã có nhân viên đăng ký.";
      }
      if (code === "LUNCH_PERMISSION_DENIED") {
        return "Bạn không có quyền thao tác chức năng quản lý thực đơn.";
      }
      if (code === "INVALID_LUNCH_DATE") {
        return "Ngày thực đơn không hợp lệ.";
      }
      return result.message || (error && error.message) || fallback;
    },
    showApiError(error, fallback) {
      const message = this.getApiErrorMessage(error, fallback);
      this.showToast("error", message);
    },
    showToast(type, message) {
      if (this.$message) {
        this.$message({
          type,
          message,
          duration: 2500,
          showClose: true
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.lunch-schedule-page {
  box-sizing: border-box;
  min-height: 100%;
  min-width: 0;
  padding-right: 10px;
  padding-top: 20px;
  font-family: UTM Avo;
  font-size: 14px;

  > .card {
    margin-bottom: 0;
  }

  .main-card-body {
    display: flex;
    min-height: 0 !important;
    flex-direction: column;
    padding-right: 10px;
  }
}

.lunch-schedule-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.lunch-schedule-header__content {
  min-width: 0;
}

.lunch-schedule-header__actions {
  display: flex;
  flex: none;
  align-items: center;
  gap: 10px;
}

.lunch-schedule-header__content .card-header-title {
  font-size: 20px;
}

.lunch-schedule-subtitle {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 16px;
  line-height: 1.35;
}

.lunch-workspace {
  display: grid;
  min-width: 0;
  align-items: start;
  grid-template-columns: minmax(280px, 30%) minmax(0, 1fr);
  gap: 16px;
}

.lunch-schedule-card {
  min-width: 0;
  border: 1px solid #edf0f5;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(31, 41, 55, 0.06);
  padding: 18px;
}

.lunch-calendar-panel {
  padding: 16px;
  position: sticky;
  top: 14px;
}

.lunch-section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.lunch-section-title {
  display: grid;
  gap: 4px;
  margin-bottom: 14px;
  min-width: 0;

  h5 {
    margin: 0;
    color: #111827;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0;
  }

  span {
    color: #6b7280;
    font-size: 13px;
  }
}

.lunch-section-heading .lunch-section-title {
  margin-bottom: 0;
}

.lunch-food-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.lunch-food-heading .lunch-section-title {
  margin-bottom: 0;
}

.lunch-primary-button,
.lunch-outline-button {
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  cursor: pointer;
  font-family: UTM Avo;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  padding: 8px 13px;
  transition: background-color 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.58;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
}

.lunch-primary-button {
  border: 1px solid var(--orange);
  background: var(--orange);
  color: #fff;

  i {
    color: #fff;
  }

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    border-color: #f58245;
    background: #f58245;
    color: #fff;
    outline: none;
  }
}

.lunch-outline-button {
  border: 1px solid var(--orange);
  background: #fff;
  color: var(--orange);

  i {
    color: var(--orange);
  }

  &:hover,
  &:focus {
    background: var(--orange_background);
    color: var(--orange);
    outline: none;
  }
}

.lunch-danger-button {
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid #ff6b6b;
  border-radius: 8px;
  background: #fff;
  color: #ff4d4f;
  cursor: pointer;
  font-family: UTM Avo;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  padding: 8px 13px;
  transition: background-color 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
  white-space: nowrap;

  i {
    color: #ff4d4f;
  }

  &:hover,
  &:focus {
    border-color: #ff4d4f;
    background: #fff1f0;
    color: #ff4d4f;
    outline: none;
  }

  &:active {
    transform: translateY(1px);
  }
}

.lunch-dialog-footer {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.lunch-dialog-footer__actions {
  display: inline-flex;
  justify-content: flex-end;
  gap: 8px;
}

.lunch-month-select-button {
  display: inline-flex;
  min-height: 34px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid #f4b17e;
  border-radius: 8px;
  background: #fff;
  color: var(--orange);
  cursor: pointer;
  font-family: UTM Avo;
  font-size: 13px;
  line-height: 1.2;
  padding: 7px 10px;
  white-space: nowrap;

  &:hover,
  &:focus {
    background: var(--orange_background);
    outline: none;
  }
}

.lunch-month-select-button--active {
  border-color: var(--orange);
  background: var(--orange_background);
}

.lunch-calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;

  h5 {
    margin: 0;
    color: #111827;
    font-size: 16px;
    font-weight: 500;
  }
}

.lunch-calendar-nav {
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #4b5563;
  cursor: pointer;

  &:hover {
    border-color: var(--orange);
    color: var(--orange);
  }
}

.lunch-weekday-grid,
.lunch-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}

.lunch-weekday-grid {
  margin-bottom: 8px;

  span {
    color: #6b7280;
    font-size: 12px;
    font-weight: 500;
    text-align: center;
  }
}

.lunch-calendar-day {
  position: relative;
  display: flex;
  min-width: 0;
  height: 42px;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 8px;
  background: #fff;
  color: #111827;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.16s ease;

  &:hover:not(:disabled) {
    border-color: #f4b17e;
    background: #fffaf4;
  }

  &:disabled {
    cursor: default;
  }
}

.lunch-calendar-day--weekend:not(.lunch-calendar-day--selected) {
  background: #f9fafb;
  color: #b7bfcc;
}

.lunch-calendar-day--holiday:not(.lunch-calendar-day--selected) {
  background: #f9fafb;
  color: #9ca3af;
}

.lunch-calendar-day--menu:not(.lunch-calendar-day--selected) {
  background: #f9fafb;
}

.lunch-calendar-day--selected {
  border-color: var(--orange);
  background: var(--orange_background);
  color: var(--orange);
  font-weight: 500;
}

.lunch-calendar-day--today:not(.lunch-calendar-day--selected) {
  font-weight: 500;
}

.lunch-calendar-day--placeholder {
  visibility: hidden;
}

.lunch-calendar-day__menu-mark {
  position: absolute;
  right: 5px;
  bottom: 4px;
  color: #16a34a;
  font-size: 10px;
  line-height: 1;
}

.lunch-calendar-legend {
  margin-top: 12px;
  color: #6b7280;
  font-size: 12px;

  i {
    color: #16a34a;
  }
}

.lunch-filter-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.lunch-inline-warning {
  border: 1px solid #fde68a;
  border-radius: 8px;
  background: #fffbeb;
  color: #92400e;
  margin-bottom: 14px;
  padding: 12px 14px;
}

.lunch-management-toolbar,
.lunch-edit-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.lunch-filter-list {
  min-width: 0;
}

.lunch-filter-button {
  min-height: 34px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #fff;
  color: #4b5563;
  cursor: pointer;
  font-size: 13px;
  padding: 7px 13px;

  &:hover {
    border-color: var(--orange);
    color: var(--orange);
  }
}

.lunch-filter-button--active {
  border-color: var(--orange);
  background: var(--orange_background);
  color: var(--orange);
}

.lunch-search-input {
  max-width: 300px;
  flex: 0 1 300px;
}

.lunch-select-all-checkbox {
  flex: 0 0 auto;
  margin-left: auto;
  white-space: nowrap;
}

.lunch-select-all-checkbox ::v-deep .el-checkbox__label {
  color: #374151;
  font-size: 13px;
}

.lunch-food-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 220px));
  gap: 14px;
  justify-content: start;
}

.lunch-food-grid:not(.lunch-food-grid--modal) {
  max-height: 650px;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 6px;
}

.lunch-food-grid:not(.lunch-food-grid--modal)::-webkit-scrollbar {
  width: 6px;
}

.lunch-food-grid:not(.lunch-food-grid--modal)::-webkit-scrollbar-thumb {
  border-radius: 6px;
  background: #ff9b62;
}

.lunch-food-grid:not(.lunch-food-grid--modal)::-webkit-scrollbar-track {
  background: transparent;
}

.lunch-food-grid.lunch-food-grid--modal {
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  max-height: 56vh;
  overflow-y: auto;
  padding-right: 4px;

  .lunch-food-card {
    width: 100%;
  }
}

.lunch-food-card {
  display: flex;
  width: 220px;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #edf0f5;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
}

.lunch-food-card--selected {
  border-color: var(--orange);
  background: #fffaf4;
  box-shadow: 0 10px 28px rgba(241, 126, 40, 0.13);
}

.lunch-food-card__image {
  position: relative;
  aspect-ratio: 16 / 10;
  background: #f9fafb;
  overflow: hidden;

  img {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.lunch-food-card__check {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--orange);
  color: #fff;
  font-size: 12px;
}

.lunch-food-card__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 13px;

  h6 {
    margin: 4px 0 6px;
    color: #111827;
    font-family: UTM Avo;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.35;
  }

  p {
    flex: 1;
    min-height: 38px;
    margin: 0;
    color: #6b7280;
    font-size: 13px;
    line-height: 1.45;
  }
}

.lunch-food-card__category {
  color: var(--orange);
  font-size: 12px;
  font-weight: 500;
}

.lunch-food-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 14px;

  strong {
    color: #111827;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }
}

.lunch-select-button {
  display: inline-flex;
  min-width: 104px;
  height: 34px;
  align-items: center;
  justify-content: center;
  padding-top: 0;
  padding-bottom: 0;
  font-size: 14px;
  font-weight: 400;

  ::v-deep > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    line-height: 1;
  }

  i {
    display: inline-flex;
    width: 14px;
    height: 14px;
    align-items: center;
    justify-content: center;
    margin-right: 0;
    line-height: 14px;
  }

  .fa-plus {
    transform: translateY(1px);
  }

  span {
    line-height: 14px;
  }
}

.lunch-empty-state {
  border: 1px dashed #d8dde6;
  border-radius: 8px;
  color: #6b7280;
  padding: 24px;
  text-align: center;
}

.lunch-save-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: none;
  gap: 12px;
}

.lunch-save-button {
  min-width: 170px;
}

@media (max-width: 1199px) {
  .lunch-workspace {
    grid-template-columns: minmax(260px, 36%) minmax(0, 1fr);
  }
}

@media (max-width: 991px) {
  .lunch-workspace {
    grid-template-columns: 1fr;
  }

  .lunch-calendar-panel {
    position: static;
  }

  .lunch-food-grid:not(.lunch-food-grid--modal) {
    max-height: 60vh;
  }

  .lunch-management-toolbar,
  .lunch-edit-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .lunch-select-all-checkbox {
    margin-left: 0;
  }

  .lunch-search-input {
    max-width: none;
    flex-basis: auto;
    width: 100%;
  }
}

@media (max-width: 767px) {
  .lunch-schedule-page {
    padding-right: 0;
  }

  .lunch-schedule-page .main-card-body {
    padding-right: 16px;
  }

  .lunch-schedule-header {
    align-items: stretch;
    flex-direction: column;
  }

  .lunch-schedule-header__actions {
    align-items: stretch;
    flex-direction: column;
  }

  .lunch-section-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .lunch-food-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .lunch-primary-button,
  .lunch-outline-button,
  .lunch-danger-button,
  .lunch-month-select-button,
  .lunch-save-button {
    width: 100%;
  }

  .lunch-filter-list {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 2px;
  }

  .lunch-food-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 180px), 180px));
    justify-content: start;
    gap: 10px;
  }

  .lunch-food-card {
    width: 180px;
  }

  .lunch-food-card__footer,
  .lunch-save-row {
    align-items: stretch;
    flex-direction: column;
  }

  .lunch-select-button {
    width: 100%;
  }

  .lunch-dialog-footer {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }

  .lunch-dialog-footer__actions {
    display: grid;
    width: 100%;
    gap: 8px;
  }
}

@media (max-width: 420px) {
  .lunch-food-grid {
    grid-template-columns: 1fr;
  }

  .lunch-food-card {
    width: 100%;
  }

  .lunch-weekday-grid,
  .lunch-calendar-grid {
    gap: 4px;
  }

  .lunch-calendar-day {
    height: 38px;
  }
}
</style>

<style lang="scss">
.lunch-menu-edit-dialog {
  border-radius: 10px;
}

.lunch-menu-edit-dialog .el-dialog__body {
  padding-bottom: 12px;
}

.lunch-menu-overwrite-confirm .el-message-box__message {
  padding-left: 0;
  white-space: pre-line;
}

.lunch-menu-overwrite-confirm .el-message-box__status {
  display: none;
}

.lunch-menu-overwrite-confirm {
  overflow: hidden;
  border: none;
  border-radius: 10px;
}

.lunch-menu-overwrite-confirm .el-message-box__header {
  background: #fb985b;
  padding: 15px 20px;
}

.lunch-menu-overwrite-confirm .el-message-box__title {
  display: flex;
  align-items: center;
  color: #fff;
  font-weight: 600;
}

.lunch-menu-overwrite-confirm .el-message-box__headerbtn {
  top: 15px;
  right: 18px;
}

.lunch-menu-overwrite-confirm .el-message-box__headerbtn .el-message-box__close {
  color: #fff;
}

.lunch-menu-overwrite-confirm .el-message-box__headerbtn:hover .el-message-box__close,
.lunch-menu-overwrite-confirm .el-message-box__headerbtn:focus .el-message-box__close {
  color: #fff;
  opacity: 0.78;
}

.lunch-overwrite-message {
  display: grid;
  gap: 14px;
}

.lunch-overwrite-message strong {
  color: #374151;
  font-weight: 600;
}

.lunch-overwrite-message__dates {
  display: grid;
  gap: 4px;
  padding-left: 18px;
}

@media (max-width: 960px) {
  .lunch-menu-edit-dialog {
    width: calc(100vw - 24px) !important;
  }
}
</style>
